'use client'

import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'

import { isLocal } from '@/lib/hostname'

const isValidSidecarDomain = (hostname: string): boolean => {
  if (hostname === 'sidecarhealth.com') return true
  if (hostname.endsWith('.sidecarhealth.com')) {
    const before = hostname.slice(0, -'.sidecarhealth.com'.length)
    return before.length > 0 && !before.includes('.')
  }
  return false
}

const isProdHost = (hostname: string): boolean => {
  const onSidecar = isValidSidecarDomain(hostname)
  const notLowerEnv =
    !isLocal(hostname) &&
    !hostname.startsWith('dev-') &&
    !hostname.startsWith('qa-') &&
    !hostname.startsWith('staging-')
  return onSidecar && notLowerEnv
}

const getEnv = (hostname: string): string => {
  if (isValidSidecarDomain(hostname)) {
    if (hostname.includes('-')) return hostname.split('-')[0]
    return 'prod'
  }
  return 'n/a'
}

/**
 * Resolve the portal type. Prefers the `appType` prop (set by the server layout
 * from runtime APP_TYPE, which differs per ECS service) over
 * NEXT_PUBLIC_APP_TYPE (build-time fallback). Both portals share a single
 * Docker image, so the build-time value cannot reliably distinguish them.
 */
const resolvePortalType = (
  appTypeProp: 'broker' | 'group' | undefined,
): 'broker' | 'group' => {
  if (appTypeProp) return appTypeProp
  const buildType = process.env.NEXT_PUBLIC_APP_TYPE
  if (buildType === 'broker' || buildType === 'group') return buildType
  return 'group'
}

const getServiceName = (_portalType: 'broker' | 'group'): string => 'group-app'

const getApplicationId = (
  portalType: 'broker' | 'group',
): string | undefined =>
  portalType === 'broker'
    ? process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID_BROKER
    : process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID_GROUP

const getClientToken = (portalType: 'broker' | 'group'): string | undefined =>
  portalType === 'broker'
    ? process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN_BROKER
    : process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN_GROUP

interface WindowWithDD extends Window {
  __DATADOG_INITIALIZED__?: boolean
}

interface DatadogProviderProps {
  children: ReactNode
  /**
   * Portal type, resolved at runtime by the server layout from the APP_TYPE env
   * var. When omitted, falls back to NEXT_PUBLIC_APP_TYPE (build-time).
   */
  appType?: 'broker' | 'group'
}

export function DatadogProvider({ children, appType }: DatadogProviderProps) {
  useEffect(() => {
    if (typeof window === 'undefined') return

    const w = window as WindowWithDD
    const hostname = window.location.hostname

    if (isLocal(hostname)) {
      return
    }

    if (w.__DATADOG_INITIALIZED__) return
    if (datadogRum.getInternalContext()) {
      w.__DATADOG_INITIALIZED__ = true
      return
    }

    const portalType = resolvePortalType(appType)
    const applicationId = getApplicationId(portalType)
    const clientToken = getClientToken(portalType)

    if (!applicationId || !clientToken) {
      console.warn(
        '[Datadog] Skipped init: NEXT_PUBLIC_DATADOG_* env vars not set',
      )
      return
    }

    const env = getEnv(hostname)
    const isProd = isProdHost(hostname)
    const service = getServiceName(portalType)
    const version =
      process.env.NEXT_PUBLIC_APP_VERSION_SHA ||
      process.env.NEXT_PUBLIC_APP_VERSION ||
      'unknown'

    datadogRum.init({
      applicationId,
      clientToken,
      site: process.env.NEXT_PUBLIC_DATADOG_SITE ?? 'datadoghq.com',
      service,
      env,
      version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 0,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      defaultPrivacyLevel: 'mask-user-input',
      traceSampleRate: isProd ? 100 : 10,
      allowedTracingUrls: [/https:\/\/.*\.sidecarhealth\.com/],
    })

    datadogLogs.init({
      clientToken,
      site: process.env.NEXT_PUBLIC_DATADOG_SITE ?? 'datadoghq.com',
      service,
      env,
      version,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ['error', 'warn'],
      sessionSampleRate: 100,
      beforeSend: log => {
        const msg = log?.message ?? ''
        if (msg.includes('Genesys')) return false
        if (msg.includes('Oops')) return false
        return true
      },
    })

    w.__DATADOG_INITIALIZED__ = true
  }, [appType])

  return <>{children}</>
}
